/*
 * Copyright (c) 2024. Bluemate B.V.
 */
import './style/searchVolumePage.scss';

import {AwesomeIcon, ChangeFunction, Checkbox, Loader, remToPx, SelectItemShape} from '@dvrd/dvr-controls';

import React, {KeyboardEventHandler, MouseEventHandler, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {
    BluematePlotSeries,
    BolSearchTerm,
    SearchVolumeData,
    SearchVolumePeriod,
    SearchVolumePeriodType
} from '../../util/interfaces';

import {BluemateButton, BluemateInput, BluemateSelect} from '../controls';

import {parseThousandSeparator} from '../../util/utils';

import SearchVolumeBarChart from './searchVolumeBarChart';

import SearchVolumeHeatChart from './searchVolumeHeatChart';

import SearchVolumePieChart from './searchVolumePieChart';

import SearchVolumeTreeChart from './searchVolumeTreeChart';

import {BluematePlot} from '../controls';

import classNames from 'classnames';

import ArrowDown from '../misc/arrowDown';

import ArrowUp from '../misc/arrowUp';

import BluematePlaceholderOverlay from '../misc/bluematePlaceholderOverlay';

import Image from '../misc/image';

import {DotsLoader} from '../controls/loader/dotsLoader';

import InfoBanner from '../misc/infoBanner';

interface Props {
    onChangeSearchTerm: ChangeFunction<string>;
    onSearch: MouseEventHandler & KeyboardEventHandler;
    onClickRelated: (term: string) => MouseEventHandler;
    onChangePeriod: ChangeFunction<SearchVolumePeriodType>;
    onRemoveTerm: (term: string | null) => MouseEventHandler;
    onChangeCombineTerms: ChangeFunction<boolean>;
    onChangeAISearchTerm: ChangeFunction<string>;
    searchTerm: string;
    searchTerms: Array<string>;
    searchVolumes: Array<SearchVolumeData>;
    comparingVolumes: Array<SearchVolumeData>;
    historicVolumes: Array<SearchVolumeData>;
    relatedTerms: Array<BolSearchTerm>;
    periodType: SearchVolumePeriodType;
    loading: boolean;
    loadingHistoric: boolean;
    combineTerms: boolean;
    aiSearchTerm: string;
    loadingAI: boolean;
    aiAnalysis: string;
}

const chartsHeight = remToPx(23.5);
//bolt-lightning

export default function SearchVolumePage(props: Props) {
    const {t} = useTranslation();
    const periodItems: Array<SelectItemShape> = [
        {label: t('searchVolume.period.lastWeek', 'Laatste week'), value: SearchVolumePeriodType.LAST_WEEK},
        {label: t('searchVolume.period.last14Days', 'Laatste 14 dagen'), value: SearchVolumePeriodType.LAST_14_DAYS},
        {label: t('searchVolume.period.lastMonth', 'Laatste maand'), value: SearchVolumePeriodType.LAST_MONTH},
        {label: t('searchVolume.period.last3Months', 'Laatste 3 maanden'), value: SearchVolumePeriodType.LAST_3_MONTHS},
        {label: t('searchVolume.period.last6Months', 'Laatste 6 maanden'), value: SearchVolumePeriodType.LAST_6_MONTHS},
        {label: t('searchVolume.period.lastYear', 'Laatste jaar'), value: SearchVolumePeriodType.LAST_YEAR},
        {label: t('searchVolume.period.last2Years', 'Laatste 2 jaar'), value: SearchVolumePeriodType.LAST_2_YEARS}
    ];
    const {
        searchVolumes,
        historicVolumes,
        loadingHistoric,
        onSearch,
        loading,
        searchTerms,
        onChangeSearchTerm,
        searchTerm,
        relatedTerms,
        onClickRelated,
        periodType,
        onChangePeriod,
        onRemoveTerm,
        comparingVolumes,
        combineTerms,
        onChangeCombineTerms,
        onChangeAISearchTerm,
        aiSearchTerm,
        loadingAI,
        aiAnalysis
    } = props;
    const comparingTotalValue = useMemo(() => {
        let totalVolume = 0;
        for (const searchVolume of comparingVolumes) {
            totalVolume += searchVolume.periods.reduce(
                (current: number, period: SearchVolumePeriod) => current + period.total,
                0
            );
        }
        return totalVolume;
    }, [comparingVolumes]);
    const [averageVolume, totalVolume, totalDiffPercentage] = useMemo(() => {
        let averageVolume = 0;
        let totalVolume = 0;
        for (const searchVolume of searchVolumes) {
            const total = searchVolume.periods.reduce(
                (current: number, period: SearchVolumePeriod) => current + period.total,
                0
            );
            totalVolume += total;
            averageVolume += Math.floor(total / searchVolume.periods.length);
        }
        let totalDiffPercentage = 0;
        if (totalVolume === 0 && comparingTotalValue > 0) totalDiffPercentage = -100;
        else if (totalVolume > 0 && comparingTotalValue === 0) totalDiffPercentage = 0;
        else if (totalVolume > 0 && comparingTotalValue > 0)
            totalDiffPercentage = Math.floor(((totalVolume - comparingTotalValue) / comparingTotalValue) * 100);
        return [averageVolume, totalVolume, totalDiffPercentage];
    }, [searchVolumes, comparingTotalValue]);
    const periodLabel = useMemo(() => {
        if (periodType === SearchVolumePeriodType.LAST_WEEK) return t('searchVolume.periodLabel.7days', '7 dagen');
        else if (periodType === SearchVolumePeriodType.LAST_14_DAYS)
            return t('searchVolume.periodLabel.14days', '14 dagen');
        else if (periodType === SearchVolumePeriodType.LAST_MONTH)
            return t('searchVolume.periodLabel.31days', '31 dagen');
        else if (periodType === SearchVolumePeriodType.LAST_3_MONTHS)
            return t('searchVolume.periodLabel.3months', '3 maanden');
        else if (periodType === SearchVolumePeriodType.LAST_6_MONTHS)
            return t('searchVolume.periodLabel.6months', '6 maanden');
        else if (periodType === SearchVolumePeriodType.LAST_YEAR)
            return t('searchVolume.periodLabel.12months', '12 maanden');
        else if (periodType === SearchVolumePeriodType.LAST_2_YEARS)
            return t('searchVolume.periodLabel.24months', '24 maanden');
    }, [periodType]);
    const aiItems = useMemo(() => {
        const items: Array<SelectItemShape> = [
            {label: t('searchVolume.chooseSearchTerm', 'Kies een zoekterm'), value: '', selectable: false}
        ];
        return items.concat(searchTerms.map((term: string) => ({value: term, label: term})));
    }, [searchTerms]);
    const combinedLabel = useMemo(
        () => (searchTerms.length > 1 ? t('searchVolume.combinedSuffix', ' - Opgeteld') : ''),
        [searchTerms]
    );

    function onClickAIAnalyse() {
        if (!searchTerms.length) return;
        onChangeAISearchTerm(searchTerms[0]);
    }

    function renderSearch() {
        const hasSearchTerms = searchTerms.length > 0;
        return (
            <div className='search-container block'>
                <div className='block-header'>
                    <label className='block-title'>
                        {t('searchVolume.title', 'Vind zoekvolume en gerelateerde zoekwoorden')}
                    </label>
                </div>
                <div className={classNames('block-content', hasSearchTerms && 'with-terms')} id='search-block'>
                    <Loader active={loading} />
                    <div className='search-container'>
                        <BluemateInput
                            onChange={onChangeSearchTerm}
                            value={searchTerm}
                            label={t('searchVolume.keyword', 'Zoekwoord')}
                            onEnter={onSearch}
                            placeholder={t('searchVolume.keywordPlaceholder', 'Voer hier een zoekwoord in')}
                            id='search-volume-term-field'
                            className='search-field'
                            ornaments={{element: <AwesomeIcon name='search' />}}
                            autoFocus
                        />
                        <Checkbox
                            className='combine-check'
                            checked={combineTerms}
                            onCheck={onChangeCombineTerms}
                            label={t('searchVolume.combineTerms', 'Meerdere zoekwoorden combineren')}
                        />
                    </div>
                    <div className='terms-container'>
                        {searchTerms.map(renderTerm)}
                        {searchTerms.length > 2 && (
                            <div className='search-term all' onClick={onRemoveTerm(null)}>
                                <label>{t('searchVolume.removeAll', 'Alles verwijderen')}</label>
                                <AwesomeIcon name='delete-left' />
                            </div>
                        )}
                    </div>
                    <div className='period-row'>
                        <BluemateSelect
                            onChange={onChangePeriod}
                            value={periodType}
                            items={periodItems}
                            label={t('searchVolume.period.label', 'Periode')}
                        />
                        <BluemateButton onClick={onSearch} label={t('searchVolume.search', 'Zoeken')} />
                    </div>
                </div>
            </div>
        );
    }

    function renderRelatedTerms() {
        return (
            <div className='related-terms block'>
                <div className='block-header'>
                    <label className='block-title'>{t('searchVolume.relatedTerms', 'Gerelateerde zoekwoorden')}</label>
                </div>
                <div className='block-content' id='related-terms-block'>
                    {!searchVolumes.length && <BluematePlaceholderOverlay />}
                    <div className='related-terms-list'>{relatedTerms.map(renderRelatedTerm)}</div>
                </div>
            </div>
        );
    }

    function renderRelatedTerm(term: BolSearchTerm, index: number) {
        return (
            <div key={index} className='related-search-term' onClick={onClickRelated(term.search_term)}>
                <label className='term'>{term.search_term}</label>
                <label className='total'>{parseThousandSeparator(term.total, ',')}</label>
            </div>
        );
    }

    function renderTerm(term: string, index: number) {
        return (
            <div key={index} className='search-term' onClick={onRemoveTerm(term)}>
                <label>{term}</label>
                <AwesomeIcon name='circle-xmark' prefix='far' />
            </div>
        );
    }

    function renderStats() {
        const negativeComparison = totalDiffPercentage < 0;
        const comparisonIcon = negativeComparison ? <ArrowDown /> : <ArrowUp />;
        const comparisonLabel = `${negativeComparison ? '-' : '+'}${Math.abs(totalDiffPercentage)} %`;
        return (
            <div className='stats-row page-row'>
                <div className='stat-container block'>
                    <div className='block-header'>
                        <label className='block-title'>
                            {t('searchVolume.averageMonthly', 'Gemiddeld zoekvolume per maand')}
                            {combinedLabel}
                        </label>
                    </div>
                    <div className='block-content'>
                        <label className='stats-value'>{parseThousandSeparator(averageVolume, ',')}</label>
                    </div>
                </div>
                <div className='stat-container block'>
                    <div className='block-header'>
                        <label className='block-title'>
                            {t('searchVolume.totalVolume', 'Totaal zoekvolume')} ({periodLabel}){combinedLabel}
                        </label>
                    </div>
                    <div className='block-content col-2'>
                        <label className='stats-value'>{parseThousandSeparator(totalVolume, ',')}</label>
                        <label className='comparison-value'>
                            {comparisonIcon}&nbsp;
                            <span>{comparisonLabel}</span>
                        </label>
                    </div>
                </div>
                <div className='stat-container block'>
                    <div className='block-header'>
                        <label className='block-title'>
                            {t('searchVolume.totalVolumePrevious', 'Totaal zoekvolume (voorgaande')} {periodLabel})
                            {combinedLabel}
                        </label>
                    </div>
                    <div className='block-content'>
                        <label className='stats-value'>{parseThousandSeparator(comparingTotalValue, ',')}</label>
                    </div>
                </div>
            </div>
        );
    }

    function renderAI() {
        const actionsDisabled = loading || loadingAI;
        return (
            <div className='block'>
                <div
                    className={classNames('block-header ai-analysis-header', searchVolumes.length > 0 && 'with-action')}
                >
                    <label className='block-title'>
                        {t('searchVolume.aiAnalysis', 'Markttrendanalyse met BluemateAI')}
                    </label>
                    <div className='ai-action'>
                        {searchTerms.length > 1 && (
                            <BluemateSelect
                                onChange={onChangeAISearchTerm}
                                value={aiSearchTerm}
                                items={aiItems}
                                disabled={actionsDisabled}
                            />
                        )}
                    </div>
                </div>
                <div className='block-content ai-content'>
                    {loadingAI && (
                        <div className='ai-loading-container'>
                            <DotsLoader dotSize='3rem' />
                        </div>
                    )}
                    {!searchVolumes.length || (!aiAnalysis.length && searchTerms.length > 1) ? (
                        !aiAnalysis.length ? (
                            <BluematePlaceholderOverlay />
                        ) : null
                    ) : (
                        !aiAnalysis.length &&
                        searchTerms.length <= 1 && (
                            <div className='button-container'>
                                <BluemateButton
                                    onClick={onClickAIAnalyse}
                                    label={t('searchVolume.analyzeBtn', 'Analyseren met BluemateAI')}
                                    ornaments={{
                                        element: (
                                            <Image
                                                name='logo-no-text'
                                                alt=''
                                                style={{
                                                    maxWidth: '1.2rem',
                                                    maxHeight: '1.2rem',
                                                    objectFit: 'scale-down'
                                                }}
                                            />
                                        )
                                    }}
                                    secondary
                                />
                            </div>
                        )
                    )}
                    <p className='ai-analysis'>{aiAnalysis}</p>
                </div>
            </div>
        );
    }

    function renderBarChart() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>{t('searchVolume.volume', 'Zoekvolume')}</label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BluematePlaceholderOverlay />}
                    <SearchVolumeBarChart searchVolumes={searchVolumes} height={chartsHeight} />
                </div>
            </div>
        );
    }

    function renderTreeMap() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>{t('searchVolume.relatedTerms', 'Gerelateerde zoekwoorden')}</label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BluematePlaceholderOverlay />}
                    <SearchVolumeTreeChart
                        onClickRelated={onClickRelated}
                        relatedTerms={relatedTerms}
                        height={chartsHeight}
                    />
                </div>
            </div>
        );
    }

    function renderPieChart() {
        return (
            <div className='block pie'>
                <div className='block-header'>
                    <label className='block-title'>
                        {t('searchVolume.nlVsBeTotal', 'Nederland vs België totaal')}
                        {combinedLabel}
                    </label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BluematePlaceholderOverlay />}
                    <SearchVolumePieChart searchVolumes={searchVolumes} height={chartsHeight} />
                </div>
            </div>
        );
    }

    function renderHeatMap() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>
                        {t('searchVolume.nlVsBePeriod', 'Nederland vs België per periode')}
                        {combinedLabel}
                    </label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BluematePlaceholderOverlay />}
                    <SearchVolumeHeatChart searchVolumes={searchVolumes} height={chartsHeight} />
                </div>
            </div>
        );
    }

    function renderLineChart() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>
                        {t('searchVolume.seasonal', 'Seizoensgebonden (4 jaar historische data)')}
                    </label>
                </div>
                <div className='block-content'>
                    <Loader active={loadingHistoric} />
                    {!historicVolumes.length && <BluematePlaceholderOverlay />}
                    <BluematePlot
                        series={historicVolumes.map((sv: SearchVolumeData): BluematePlotSeries => ({
                            id: sv.search_term,
                            name: sv.search_term,
                            data: sv.periods.map(p => ({date: p.period, value: p.total}))
                        }))}
                        type='area'
                        height={chartsHeight}
                        legend={false}
                        borderless
                        options={{yaxis: {min: 0}}}
                    />
                </div>
            </div>
        );
    }

    useEffect(() => {
        window.setTimeout(() => {
            const searchContainer = document.getElementById('search-block');
            const relatedTermsContainer = document.getElementById('related-terms-block');
            if (!searchContainer || !relatedTermsContainer) return;
            relatedTermsContainer.style.height = searchContainer.getBoundingClientRect().height + 'px';
        }, 200);
    }, [searchTerms]);

    return (
        <>
            <InfoBanner
                featureKey='search_volume'
                variant='info'
                title={t('searchVolume.banner.title', 'Zoekvolume')}
                showForNewUsersOnly={true}
            >
                {t('searchVolume.banner.body', 'Bekijk de zoekvolumes op bol. Vergelijk termen om kansen te vinden.')}
            </InfoBanner>
            <div className='search-volume-page'>
                <div className='search-row page-row'>
                    {renderSearch()}
                    {renderRelatedTerms()}
                </div>
                {renderStats()}
                <div className='page-row ai-bar-row'>
                    {renderAI()}
                    {renderBarChart()}
                </div>
                <div className='charts-row page-row'>
                    {renderTreeMap()}
                    {renderPieChart()}
                </div>
                {renderHeatMap()}
                {renderLineChart()}
            </div>
        </>
    );
}
